import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight, Shield, Layers, Brain, Sparkles, Building2, Stethoscope,
  ShoppingBag, Leaf, Calculator, Globe, CheckCircle2, Code2, Rocket, Heart,
  ExternalLink, ChevronLeft, ChevronRight,
} from "lucide-react";
import PageMeta from "../components/PageMeta";
import { useCurrency } from "../context/CurrencyContext";

const GEMINII_APPS = [
  {
    name: "Rogue",
    tag: "Live",
    tagColor: "bg-rogue-emerald/15 text-rogue-emerald border-rogue-emerald/25",
    desc: "Multi-tenant SaaS for operators. CRM, Ops, Teams, Stockroom, Bookkeep, Support — and Rogi (Cerebrum AI) inside every module.",
    to: "/space/rogue",
    logo: "/logos/rogue/white-blue.png",
    accent: "border-rogue-cyan/25",
    available: true,
  },
  {
    name: "Kinly",
    tag: "Launching July 2026 · Join waitlist",
    desc: "The first Geminii Space app for families. One app for calendar, photos, chores, chat, and AI that knows your family. Rogue is for operators — Kinly is for the people they go home to.",
    to: "/space/kinly",
    icon: Heart,
    available: true,
    customVisual: "kinly",
  },
  {
    name: "Geminii Security",
    tag: "2026",
    tagColor: "bg-white/[0.06] text-geminii-muted border-white/[0.1]",
    desc: "Intelligent security management for modern businesses — site monitoring, incident response, compliance, and patrol coordination.",
    to: "/space",
    icon: Shield,
    accent: "border-white/[0.06]",
    available: false,
  },
  {
    name: "Geminii Construction",
    tag: "2026",
    tagColor: "bg-white/[0.06] text-geminii-muted border-white/[0.1]",
    desc: "Project management built for the construction industry — phase tracking, subcontractor coordination, materials, and safety compliance.",
    to: "/space",
    icon: Building2,
    accent: "border-white/[0.06]",
    available: false,
  },
  {
    name: "Geminii Medical",
    tag: "Roadmap",
    tagColor: "bg-white/[0.06] text-geminii-muted border-white/[0.1]",
    desc: "Practice management for clinics — patient records, scheduling, billing, prescriptions, and Cerebrum-powered triage support.",
    to: "/space",
    icon: Stethoscope,
    accent: "border-white/[0.06]",
    available: false,
  },
];

const CUSTOMER_APPS = [
  {
    name: "AquaShop",
    industry: "E-commerce · Online Store",
    icon: ShoppingBag,
    color: "rogue-cyan",
    desc: "Storefront, cart, payments, inventory, and order tracking — built around the brand and its catalogue.",
    features: ["Stripe + local payment gateways", "Full inventory sync", "AI product descriptions"],
    palette: ["#1FB6C1", "#0F151A", "#FFFFFF"],
  },
  {
    name: "Verdant",
    industry: "Gardening · Services",
    icon: Leaf,
    color: "rogue-emerald",
    desc: "Booking platform for a local gardening company — seasonal scheduling, technician dispatch, customer portal.",
    features: ["Online booking + deposits", "Route-planned dispatch", "Customer review loop"],
    palette: ["#10B981", "#1A2F22", "#F0FDF4"],
  },
  {
    name: "Ledgerly",
    industry: "Accounting · Professional Services",
    icon: Calculator,
    color: "cerebrum-violet",
    desc: "Boutique accounting firm site — client portals, document drop, secure invoicing, encrypted messaging.",
    features: ["Encrypted client portal", "Doc OCR + auto-filing", "Stripe ACH invoicing"],
    palette: ["#8B5CF6", "#1A1B2E", "#F5F3FF"],
  },
];

const PROCESS = [
  { step: "01", title: "Discovery", detail: "A 30-minute call. The business, the customers, the outcome that matters. Recorded, structured by Cerebrum." },
  { step: "02", title: "Blueprint", detail: "Within a week: written scope, visual prototype, fixed-scope quote. Build proceeds only when the blueprint is approved." },
  { step: "03", title: "Build", detail: "Working software ships every two weeks. Live previews, hands-on feedback, continuous iteration." },
  { step: "04", title: "Launch & evolve", detail: "Go live. Then keep growing. Add modules, add Cerebrum, add mobile apps — Space evolves with the business." },
];

const PRICING_BANDS = [
  { tier: "Marketing site", usdFrom: 1500, blurb: "5–10 page brand site with CMS, contact forms, analytics. 3–4 weeks." },
  { tier: "Custom web app", usdFrom: 6000, blurb: "Dashboards, auth, payments, multi-user, custom workflows. 6–10 weeks.", featured: true },
  { tier: "Full platform", usdFrom: null, blurb: "Multi-tenant SaaS, mobile apps, AI features, integrations. From 3 months.", custom: true },
];

const DIFFERENTIATORS = [
  { title: "Multi-tenant from day one", desc: "Every Space project is architected so it can scale to many customers from a single codebase. Tenant data stays isolated.", icon: Layers },
  { title: "Cerebrum AI inside everything", desc: "Voice, OCR, recommendations, coaching — Cerebrum can be woven into any Space-built app or site.", icon: Brain },
  { title: "White-label flexibility", desc: "Custom domain, logo, colours, and AI persona (Rogi by default, any name by request).", icon: Sparkles },
  { title: "Production-grade engineering", desc: "The same stack that ships Geminii's own SaaS — React + FastAPI + MongoDB. Tested. Deployed. Maintained.", icon: Code2 },
];

function HidroVerseShowcase() {
  const slides = [
    { src: "/images/hidroverse/hero.jpg", label: "Home" },
    { src: "/images/hidroverse/s3.jpg",   label: "Services" },
    { src: "/images/hidroverse/s5.jpg",   label: "Industries" },
    { src: "/images/hidroverse/s4.jpg",   label: "Why HidroVerse" },
  ];
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 4500);
    return () => clearInterval(t);
  }, [paused, slides.length]);

  const prev = (e) => { e.preventDefault(); e.stopPropagation(); setIdx((i) => (i - 1 + slides.length) % slides.length); };
  const next = (e) => { e.preventDefault(); e.stopPropagation(); setIdx((i) => (i + 1) % slides.length); };

  return (
    <div
      className="relative w-full"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="hidroverse-showcase"
    >
      {/* Browser chrome */}
      <div className="rounded-xl overflow-hidden border border-white/[0.08] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.4)] bg-[#0E1318]">
        <div className="flex items-center gap-2 px-3 py-2 bg-[#161C23] border-b border-white/[0.06]">
          <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
          <div className="flex-1 mx-3 px-3 py-1 rounded-md bg-black/40 text-[10px] font-mono text-white/50 truncate text-center">
            hidroverse.co.za
            <span className="ml-2 text-[8px] text-rogue-cyan">●</span>
          </div>
        </div>

        {/* Slide */}
        <div className="relative aspect-[16/10] bg-black">
          {slides.map((s, i) => (
            <img
              key={s.src}
              src={s.src}
              alt={`HidroVerse — ${s.label}`}
              loading={i === 0 ? "eager" : "lazy"}
              className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0"}`}
            />
          ))}

          {/* Slide label badge */}
          <div className="absolute top-3 left-3 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/55 backdrop-blur-md border border-white/10">
            <span className="text-[10px] font-mono uppercase tracking-wider text-white/85">{slides[idx].label}</span>
          </div>

          {/* Prev / next */}
          <button
            onClick={prev}
            aria-label="Previous slide"
            data-testid="hv-showcase-prev"
            className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/45 backdrop-blur-md border border-white/15 text-white hover:bg-rogue-cyan hover:text-geminii-deep hover:border-rogue-cyan transition flex items-center justify-center"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={next}
            aria-label="Next slide"
            data-testid="hv-showcase-next"
            className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/45 backdrop-blur-md border border-white/15 text-white hover:bg-rogue-cyan hover:text-geminii-deep hover:border-rogue-cyan transition flex items-center justify-center"
          >
            <ChevronRight size={16} />
          </button>
        </div>

        {/* Dots */}
        <div className="flex items-center justify-center gap-1.5 px-3 py-3 bg-[#0B0F13] border-t border-white/[0.05]">
          {slides.map((s, i) => (
            <button
              key={s.label}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIdx(i); }}
              aria-label={`Go to slide ${i + 1}: ${s.label}`}
              data-testid={`hv-showcase-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-rogue-cyan" : "w-1.5 bg-white/25 hover:bg-white/50"}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}


export default function SpacePage({ onTrialClick }) {
  const { symbol, rate, currency } = useCurrency();
  const localFrom = (usd) =>
    `${symbol} ${Math.round(usd * (rate || 1)).toLocaleString()}`;

  return (
    <div className="min-h-screen" data-testid="space-page">
      <PageMeta
        title="Geminii Space"
        description="The advanced apps and websites division of Geminii. Multi-tenant SaaS by default — bespoke builds on request."
        path="/space"
      />

      {/* ==================== HERO ==================== */}
      <section data-testid="space-hero" className="relative pt-28 md:pt-36 pb-12 md:pb-16 overflow-hidden">
        <div
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage: `url(/images/hero-space.png)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-geminii-deep/30 via-geminii-deep/70 to-geminii-deep" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <img src="/logos/space/white-blue.png" alt="Geminii Space" className="h-10 sm:h-12 w-auto mb-6" data-testid="space-hero-logo" />
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rogue-cyan/10 border border-rogue-cyan/25 mb-5">
            <Sparkles size={12} className="text-rogue-cyan" />
            <span className="text-[11px] font-mono text-rogue-cyan uppercase tracking-wider">SaaS platforms · custom apps · websites that perform</span>
          </div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight max-w-4xl">
            Working software <span className="text-rogue-cyan">for working businesses.</span>
          </h1>
          <p className="mt-6 text-lg text-geminii-muted leading-relaxed max-w-2xl">
            Geminii Space is the engineering division behind every Geminii product — Rogue, Cerebrum, and the site
            rendered in this window. Multi-tenant SaaS platforms by default. Bespoke websites and custom apps for the
            businesses ready to build the future of their industry.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/space/rogue"
              data-testid="space-hero-explore-rogue"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-rogue-cyan text-geminii-deep font-semibold text-sm hover:shadow-[0_0_24px_rgba(31,182,193,0.35)] transition-all"
            >
              Explore Rogue <ArrowRight size={16} />
            </Link>
            <a
              href="#space-services"
              data-testid="space-hero-services"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/[0.12] text-white font-semibold text-sm hover:border-rogue-cyan/50 hover:text-rogue-cyan transition-all"
            >
              See the work
            </a>
          </div>
        </div>
      </section>

      {/* ==================== WHAT WE BUILD ==================== */}
      <section id="space-services" className="py-12 md:py-16 border-t border-white/[0.04]" data-testid="space-what-we-build">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-white mb-3">Two tracks. One studio.</h2>
          <p className="text-geminii-muted mb-10 max-w-xl">
            Multi-tenant SaaS for entire industries. Custom builds for businesses with their own vision.
            Same engineering DNA powering both.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="bg-geminii-card border border-white/[0.06] rounded-xl p-7">
              <div className="flex items-center gap-3 mb-3">
                <Rocket size={20} className="text-rogue-cyan" />
                <h3 className="font-display font-bold text-lg text-white">Geminii Space Apps</h3>
              </div>
              <p className="text-sm text-geminii-muted mb-4 leading-relaxed">
                Industry-defining multi-tenant SaaS, built once and served to thousands of businesses.
                Rogue leads the lineup. Security, Construction, and Medical follow through 2026.
              </p>
              <p className="text-[10px] font-mono text-geminii-dim uppercase tracking-wider mb-2">Live now</p>
              <p className="text-sm text-white">→ Rogue (CRM, Ops, Teams, Projects, Stockroom, Bookkeep, Support, Connect)</p>
              <p className="text-[10px] font-mono text-geminii-dim uppercase tracking-wider mt-4 mb-2">On the roadmap</p>
              <p className="text-sm text-white">→ Security · Construction · Medical · more</p>
            </div>
            <div className="bg-geminii-card border border-rogue-cyan/20 rounded-xl p-7 relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-rogue-cyan/5 blur-2xl pointer-events-none" />
              <div className="flex items-center gap-3 mb-3">
                <Globe size={20} className="text-rogue-cyan" />
                <h3 className="font-display font-bold text-lg text-white">Custom Sites &amp; Apps</h3>
              </div>
              <p className="text-sm text-geminii-muted mb-4 leading-relaxed">
                Powerful websites and bespoke applications, architected for ambitious businesses.
                Branded for the client, owned by the client, engineered to scale like Rogue.
              </p>
              <p className="text-[10px] font-mono text-geminii-dim uppercase tracking-wider mb-2">What gets built</p>
              <ul className="space-y-1.5 text-sm text-white">
                <li>→ Brand marketing sites with CMS</li>
                <li>→ Custom web apps & dashboards</li>
                <li>→ Multi-tenant SaaS platforms</li>
                <li>→ AI-powered customer portals</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== GEMINII SPACE APPS (RAIL) ==================== */}
      <section className="py-12 md:py-16 border-t border-white/[0.04]" data-testid="space-apps-rail">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <h2 className="font-display font-bold text-2xl md:text-3xl text-white mb-2">Geminii Space Apps</h2>
              <p className="text-geminii-muted max-w-lg">A growing portfolio of industry-defining SaaS. Multi-tenant. AI-native. Built to dominate verticals.</p>
            </div>
            <Link to="/space/rogue" className="text-sm text-rogue-cyan hover:underline">Explore Rogue →</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {GEMINII_APPS.map((app, i) => {
              /* Kinly gets a warm, on-brand mini-tile that previews its actual aesthetic */
              if (app.customVisual === "kinly") {
                return (
                  <motion.div
                    key={app.name}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08 }}
                    className="relative rounded-xl overflow-hidden border border-kinly-line bg-kinly-bg hover:border-kinly-coral/40 transition-colors"
                    data-testid="space-app-kinly"
                  >
                    {/* Peach atmospheric glow */}
                    <div className="pointer-events-none absolute top-0 right-0 w-3/4 h-3/4" aria-hidden="true" style={{
                      background: "radial-gradient(ellipse 60% 60% at 80% 20%, #FBE9DF 0%, transparent 65%)",
                      filter: "blur(20px)",
                    }} />
                    <div className="relative p-6">
                      <div className="flex items-start justify-between mb-5">
                        <div className="flex items-center gap-2.5">
                          <div className="w-9 h-9 rounded-full bg-kinly-coral flex items-center justify-center shadow-[0_2px_6px_-2px_rgba(224,120,86,0.4)]">
                            <span className="font-kinly text-white font-medium text-[18px] leading-none -mt-px">k</span>
                          </div>
                          <span className="font-kinly font-medium text-[18px] text-kinly-ink">Kinly</span>
                        </div>
                        <span className="text-[10px] font-mono px-2 py-1 rounded-full border border-kinly-coral/30 bg-white/60 text-kinly-coral uppercase tracking-wider">
                          July 2026 · waitlist
                        </span>
                      </div>
                      <p className="font-kinly font-medium text-[22px] text-kinly-ink leading-[1.15] tracking-[-0.018em] mb-3">
                        Your family's<br/>quiet co-pilot.
                      </p>
                      <p className="text-[13px] text-kinly-ink2 leading-[1.55] mb-5 max-w-[28rem]">
                        {app.desc}
                      </p>
                      {/* Mini event-card preview — echoes the real app */}
                      <div className="rounded-xl bg-white border border-kinly-line p-3 mb-5 max-w-[19rem]">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-kinly-coral" />
                          <p className="text-[9px] font-medium uppercase tracking-[0.16em] text-kinly-ink2">16:00 · Lily</p>
                        </div>
                        <p className="text-[12px] text-kinly-ink font-medium leading-tight">Piano · Mrs Smith</p>
                      </div>
                      <Link
                        to={app.to}
                        data-testid="space-app-kinly-cta"
                        className="inline-flex items-center gap-1.5 font-kinly text-[13px] font-medium text-kinly-ink hover:text-kinly-coral transition-colors"
                      >
                        Visit Kinly <ArrowRight size={14} />
                      </Link>
                    </div>
                  </motion.div>
                );
              }

              /* Default dark tile for everything else */
              return (
                <motion.div
                  key={app.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className={`bg-geminii-card border ${app.accent || "border-white/[0.06]"} rounded-xl p-6 ${app.available ? "hover:border-rogue-cyan/40 transition-colors" : ""}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    {app.logo ? (
                      <img src={app.logo} alt={app.name} className="h-8 w-auto" />
                    ) : (
                      <app.icon size={28} className="text-geminii-muted" />
                    )}
                    <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${app.tagColor || "bg-white/[0.06] text-geminii-muted border-white/[0.1]"}`}>{app.tag}</span>
                  </div>
                  <h3 className="font-display font-bold text-base text-white mb-2">{app.name}</h3>
                  <p className="text-sm text-geminii-muted leading-relaxed mb-4">{app.desc}</p>
                  {app.available ? (
                    <Link to={app.to} className="inline-flex items-center gap-1 text-sm text-rogue-cyan hover:gap-2 transition-all">
                      Visit {app.name} <ArrowRight size={14} />
                    </Link>
                  ) : (
                    <span className="text-[11px] font-mono text-geminii-dim">Coming soon — <Link to="/contact?intent=early-access" className="text-rogue-cyan/80 hover:underline">request early access</Link></span>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ==================== GEMINII SPACE SITES ==================== */}
      <section className="py-12 md:py-16 border-t border-white/[0.04]" data-testid="space-sites">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <h2 className="font-display font-bold text-2xl md:text-3xl text-white mb-2">Geminii Space Sites</h2>
              <p className="text-geminii-muted max-w-lg">Custom-built marketing sites and brand presences for working businesses. Built around the business, not around a template.</p>
            </div>
            <a href="https://hidroverse.co.za" target="_blank" rel="noopener noreferrer" className="text-sm text-rogue-cyan hover:underline">Visit HidroVerse →</a>
          </div>

          {/* ───────── FLAGSHIP TILE: HidroVerse (LIVE) ───────── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <motion.a
              href="https://hidroverse.co.za"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              data-testid="space-site-hidroverse"
              className="group block relative rounded-xl overflow-hidden border border-rogue-cyan/25 hover:border-rogue-cyan/50 transition-colors bg-geminii-card"
            >
              {/* Industrial atmospheric gradient — echoes HidroVerse's hero */}
              <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{
                background: "radial-gradient(circle at 78% 25%, rgba(31,182,193,0.15) 0%, transparent 55%)",
              }} />

              <div className="relative p-6">
                {/* Header row: real HidroVerse wordmark + LIVE badge */}
                <div className="flex items-center justify-between gap-3 mb-5">
                  <div>
                    <p className="font-display font-bold text-base text-white leading-none tracking-tight">
                      Hidro<span className="text-rogue-cyan">Verse</span><sup className="text-[9px] ml-px text-rogue-cyan/70">™</sup>
                    </p>
                    <p className="text-[9px] text-geminii-muted font-mono uppercase tracking-[0.16em] mt-1.5">hidroverse.co.za</p>
                  </div>

                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rogue-cyan/10 border border-rogue-cyan/25">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rogue-cyan opacity-60" />
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-rogue-cyan" />
                    </span>
                    <span className="text-[9px] font-mono uppercase tracking-wider text-rogue-cyan font-semibold whitespace-nowrap">Live</span>
                  </div>
                </div>

                {/* The showcase — the star of the tile */}
                <HidroVerseShowcase />

                {/* CTAs */}
                <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
                  <span
                    data-testid="space-site-hidroverse-visit"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-rogue-cyan text-geminii-deep text-sm font-semibold group-hover:shadow-[0_0_18px_rgba(31,182,193,0.4)] transition-all"
                  >
                    Visit hidroverse.co.za <ExternalLink size={13} />
                  </span>
                  <Link
                    to="/contact?intent=space"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-white/[0.04] border border-white/[0.08] text-white text-sm font-medium hover:border-rogue-cyan/40 hover:text-rogue-cyan transition-all"
                  >
                    Build something like this <ArrowRight size={13} />
                  </Link>
                </div>
              </div>
            </motion.a>

            {/* Empty placeholder — keeps the 2-col rhythm of the Apps grid */}
            <div className="hidden md:flex rounded-xl border border-dashed border-white/[0.06] items-center justify-center min-h-[200px]" data-testid="space-sites-coming-soon">
              <div className="text-center px-6 py-10">
                <Code2 size={20} className="text-geminii-dim mx-auto mb-3" />
                <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-geminii-muted mb-1.5">Next flagship</p>
                <p className="text-sm text-geminii-dim leading-relaxed max-w-[18rem]">
                  Your business could be here. We only show production sites we've built.
                </p>
                <Link to="/contact?intent=space" className="inline-flex items-center gap-1 text-xs text-rogue-cyan hover:gap-2 transition-all mt-4 font-medium">
                  Start a brief <ArrowRight size={12} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== PROCESS ==================== */}
      <section className="py-12 md:py-16 border-t border-white/[0.04]" data-testid="space-process">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-white mb-3">From discovery to launch · fast</h2>
          <p className="text-geminii-muted mb-10 max-w-xl">
            Four phases. Predictable cadence. Visible progress every week.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            {PROCESS.map((p, i) => (
              <motion.div
                key={p.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-geminii-card border border-white/[0.06] rounded-xl p-6"
              >
                <span className="font-mono text-[10px] text-rogue-cyan tracking-wider">{p.step}</span>
                <h3 className="font-display font-bold text-base text-white mt-2 mb-2">{p.title}</h3>
                <p className="text-sm text-geminii-muted leading-relaxed">{p.detail}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== PRICING BANDS (Space custom work — distinct from Rogue) ==================== */}
      <section id="pricing-bands" className="py-12 md:py-16 border-t border-white/[0.04]" data-testid="space-pricing-bands">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-3">
            <h2 className="font-display font-bold text-2xl md:text-3xl text-white">Indicative pricing · Space custom builds</h2>
            <span className="text-[10px] font-mono text-geminii-dim uppercase tracking-wider" data-testid="space-pricing-currency">
              Shown in {currency || "USD"}
            </span>
          </div>
          <p className="text-geminii-muted mb-6 max-w-2xl">
            Bespoke projects sit outside the SaaS subscription model. Every engagement receives a fixed-scope quote
            after the discovery call. The bands below set expectations — final scope, final number.
          </p>
          {/* Disambiguation note — Space vs Rogue */}
          <div className="bg-cerebrum-violet/5 border border-cerebrum-violet/15 rounded-xl p-4 mb-8 flex items-start gap-3" data-testid="space-pricing-disambig">
            <Sparkles size={16} className="text-cerebrum-violet mt-0.5 flex-shrink-0" />
            <p className="text-sm text-geminii-muted leading-relaxed">
              <span className="text-white font-medium">Not the same as Rogue pricing.</span>{" "}
              These bands cover one-off Space builds (websites, custom apps, full platforms).
              For a Rogue SaaS subscription — see{" "}
              <Link to="/space/rogue/pricing" className="text-rogue-cyan underline-offset-4 hover:underline">Rogue pricing</Link>{" "}
              instead. Nexus and Cloud pricing live on their own pages.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {PRICING_BANDS.map((b) => (
              <div
                key={b.tier}
                data-testid={`space-band-${b.tier.toLowerCase().replace(/\s+/g, '-')}`}
                className={`bg-geminii-card border rounded-xl p-6 ${b.featured ? "border-rogue-cyan/40 ring-1 ring-rogue-cyan/15" : "border-white/[0.06]"}`}
              >
                {b.featured && (
                  <span className="inline-block text-[10px] font-mono text-rogue-cyan bg-rogue-cyan/10 px-2 py-0.5 rounded mb-3 uppercase tracking-wider">
                    Most common engagement
                  </span>
                )}
                <h3 className="font-display font-bold text-base text-white mb-1">{b.tier}</h3>
                {b.custom ? (
                  <p className="font-display font-bold text-2xl text-rogue-cyan mb-3">Quoted per scope</p>
                ) : (
                  <p className="font-display font-bold text-2xl text-rogue-cyan mb-3">
                    from {localFrom(b.usdFrom)}
                  </p>
                )}
                <p className="text-sm text-geminii-muted leading-relaxed">{b.blurb}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-geminii-dim">
            Local-currency display is based on region. Final quotes can be issued in USD, ZAR, EUR, or GBP.
            Maintenance and hosting from {localFrom(99)}/month per project.
          </p>
        </div>
      </section>

      {/* ==================== DIFFERENTIATORS ==================== */}
      <section className="py-12 md:py-16 border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-white mb-3">Why Geminii Space</h2>
          <p className="text-geminii-muted mb-10 max-w-xl">Production-grade engineering. AI-native architecture. White-label flexibility. Built to scale.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {DIFFERENTIATORS.map((d, i) => (
              <motion.div
                key={d.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-geminii-card border border-white/[0.06] rounded-xl p-6"
              >
                <d.icon size={22} className="text-rogue-cyan mb-3" />
                <h3 className="font-display font-bold text-base text-white mb-2">{d.title}</h3>
                <p className="text-sm text-geminii-muted leading-relaxed">{d.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== CLOSING ==================== */}
      <section className="py-14 md:py-20 border-t border-white/[0.04]" data-testid="space-closing">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <img src="/logos/space/white-blue.png" alt="Geminii Space" className="h-8 w-auto mx-auto mb-6 opacity-80" />
          <h2 className="font-display font-bold text-3xl md:text-4xl text-white mb-4 leading-tight">
            Software, the way it should have been built in the first place.
          </h2>
          <p className="text-geminii-muted mb-8 max-w-xl mx-auto leading-relaxed">
            From dedicated Geminii Space SaaS apps to custom-built sites and platforms — powering growth,
            powering ambition. <span className="text-white">Get powered by Geminii Space.</span>
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/space/rogue"
              data-testid="space-closing-rogue"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-rogue-cyan text-geminii-deep font-semibold text-sm hover:shadow-[0_0_24px_rgba(31,182,193,0.35)] transition-all"
            >
              Explore Rogue <ArrowRight size={16} />
            </Link>
            <Link
              to="/contact?intent=space"
              data-testid="space-closing-contact"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border border-white/[0.12] text-white font-semibold text-sm hover:border-rogue-cyan/50 hover:text-rogue-cyan transition-all"
            >
              Start a custom build
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
